import { useState, useEffect, useCallback } from 'react';
import { apiClient, useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { MediaUpload, MediaPreview } from '../components/MediaUpload';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  MapPin, Plus, Trash2, ShoppingCart, Tag, Package, Check, Minus, Lock, Pencil, Save,
  Clock, Calendar, Mail, Copy, AlertTriangle, Users, X, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const PLACEHOLDER_COLORS = [
  'from-[#FF6B35] to-[#FF3E00]',
  'from-[#FFB800] to-[#FF6B35]',
  'from-[#3b82f6] to-[#8b5cf6]',
  'from-[#10b981] to-[#3b82f6]',
  'from-[#ec4899] to-[#8b5cf6]',
  'from-[#f97316] to-[#ef4444]',
];

export default function BonPlanPage({ embedded = false }) {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [form, setForm] = useState({ product_name: '', price: '', location: '', description: '', is_group_order: false, unit: 'kg', image_url: '', deadline_commande: '', deadline_livraison: '', is_enveloppe: false });
  const [orderQty, setOrderQty] = useState({});
  const [orderEnveloppe, setOrderEnveloppe] = useState({});
  const [orderForOther, setOrderForOther] = useState({});
  const [orderBenefName, setOrderBenefName] = useState({});
  const [showLockConfirm, setShowLockConfirm] = useState(null);
  const [showRecap, setShowRecap] = useState(null);
  const [recapText, setRecapText] = useState('');

  const fetchDeals = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/deals');
      setDeals(data);
      const envState = {};
      data.forEach(d => {
        const myOrd = (d.orders || []).find(o => o.user_id === user?.id);
        if (myOrd) envState[d.id] = myOrd.is_enveloppe || false;
      });
      setOrderEnveloppe(prev => ({ ...prev, ...envState }));
    } catch { toast.error('Erreur'); } finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/deals', { ...form, price: parseFloat(form.price) || 0, image_url: form.image_url || null, deadline_commande: form.deadline_commande || null, deadline_livraison: form.deadline_livraison || null });
      toast.success('Bon plan ajoute !'); setShowCreate(false);
      setForm({ product_name: '', price: '', location: '', description: '', is_group_order: false, unit: 'kg', image_url: '', deadline_commande: '', deadline_livraison: '', is_enveloppe: false });
      fetchDeals();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editDeal) return;
    try {
      await apiClient.put(`/deals/${editDeal.id}`, { product_name: editDeal.product_name, price: parseFloat(editDeal.price) || 0, unit: editDeal.unit, location: editDeal.location, description: editDeal.description, image_url: editDeal.image_url || null, deadline_commande: editDeal.deadline_commande || null, deadline_livraison: editDeal.deadline_livraison || null, is_enveloppe: editDeal.is_enveloppe });
      toast.success('Modifie !'); setEditDeal(null); fetchDeals();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleOrder = async (dealId) => {
    const qty = orderQty[dealId] || 1;
    const enveloppe = orderEnveloppe[dealId] || false;
    const benef = orderForOther[dealId] ? (orderBenefName[dealId] || '').trim() : null;
    if (orderForOther[dealId] && !benef) { toast.error('Renseigne le prenom du beneficiaire'); return; }
    try { await apiClient.post(`/deals/${dealId}/order`, { quantity: qty, is_enveloppe: enveloppe, beneficiaire_externe: benef }); toast.success(benef ? `Commande pour ${benef} enregistree !` : 'Commande enregistree !'); setOrderForOther({...orderForOther, [dealId]: false}); setOrderBenefName({...orderBenefName, [dealId]: ''}); fetchDeals(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleTogglePaid = async (dealId, orderUserId) => {
    try { await apiClient.put(`/deals/${dealId}/order/${orderUserId}/paid`); fetchDeals(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleLock = async (dealId) => {
    try { await apiClient.post(`/deals/${dealId}/lock`); toast.success('Commande verrouillee !'); setShowLockConfirm(null); fetchDeals(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleRecap = async (dealId) => {
    try { const { data } = await apiClient.get(`/deals/${dealId}/recap`); setRecapText(data.recap); setShowRecap(dealId); }
    catch { toast.error('Erreur'); }
  };

  const handleCopyRecap = () => {
    navigator.clipboard.writeText(recapText).then(() => { toast.success('Recapitulatif copie !'); });
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ?')) return;
    try { await apiClient.delete(`/deals/${id}`); toast.success('Supprime'); setSelectedDeal(null); fetchDeals(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const isDeadlinePassed = (deal) => {
    if (!deal.deadline_commande) return false;
    try { return new Date() > new Date(deal.deadline_commande); } catch { return false; }
  };

  if (loading) return <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className={`${embedded ? "" : "min-h-screen"} bg-[#0F0F0F] ${embedded ? "pb-4" : "pb-20"}`}>
      {!embedded && (
        <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <h1 className="text-xl font-bold text-white">Bons Plans</h1>
              <p className="text-xs text-zinc-500">Offres & commandes groupees</p>
            </div>
            <Button onClick={() => setShowCreate(true)} className="bg-[#FF6B35] text-black hover:bg-[#E85A25]" size="sm"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
        </header>
      )}
      {embedded && (
        <div className="flex justify-end px-4 pt-3">
          <Button onClick={() => setShowCreate(true)} className="bg-[#FF6B35] text-black hover:bg-[#E85A25]" size="sm" data-testid="create-deal-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau</Button>
        </div>
      )}

      {/* Pinterest Grid */}
      <div className="p-3">
        {deals.length > 0 ? (
          <div className="columns-2 gap-2.5 space-y-2.5" data-testid="deals-grid">
            {deals.map((deal, idx) => {
              const orders = deal.orders || [];
              const totalQty = orders.reduce((s, o) => s + (o.quantity || 0), 0);
              const myOrder = orders.find(o => o.user_id === user?.id);
              const isLocked = deal.locked;
              const colorClass = PLACEHOLDER_COLORS[idx % PLACEHOLDER_COLORS.length];

              return (
                <div
                  key={deal.id}
                  className="break-inside-avoid cursor-pointer group"
                  onClick={() => setSelectedDeal(deal)}
                  data-testid={`deal-card-${deal.id}`}
                >
                  <div className={`rounded-xl overflow-hidden border transition-all duration-200 ${
                    isLocked ? 'border-red-500/40' : 'border-[#2A2A2E] group-hover:border-[#FF6B35]/40'
                  }`}>
                    {/* Image */}
                    <div className="relative aspect-square overflow-hidden">
                      {deal.image_url ? (
                        <img
                          src={deal.image_url}
                          alt={deal.product_name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${colorClass} flex items-center justify-center`}>
                          {deal.is_group_order
                            ? <ShoppingCart className="w-10 h-10 text-white/40" />
                            : <Tag className="w-10 h-10 text-white/40" />
                          }
                        </div>
                      )}
                      {/* Price badge */}
                      <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-lg px-2 py-1">
                        <span className="font-mono text-sm font-bold text-[#FF6B35]">{deal.price}</span>
                        <span className="text-[9px] text-zinc-400 ml-0.5">XPF/{deal.unit}</span>
                      </div>
                      {/* Status badges */}
                      <div className="absolute top-2 left-2 flex flex-col gap-1">
                        {isLocked && (
                          <span className="bg-red-500/90 backdrop-blur-sm text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5">
                            <Lock className="w-2.5 h-2.5" /> Clos
                          </span>
                        )}
                        {myOrder && (
                          <span className="bg-green-500/90 backdrop-blur-sm text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5">
                            <Check className="w-2.5 h-2.5" /> Commandé
                          </span>
                        )}
                      </div>
                      {/* Orders count overlay */}
                      {orders.length > 0 && (
                        <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm rounded-lg px-2 py-0.5 flex items-center gap-1">
                          <Users className="w-3 h-3 text-zinc-300" />
                          <span className="text-[10px] font-bold text-white">{orders.length}</span>
                          <span className="text-[9px] text-zinc-400">· {totalQty} {deal.unit}</span>
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="bg-[#1B1B1B] p-2.5">
                      <h3 className="text-white font-bold text-xs truncate">{deal.product_name}</h3>
                      <div className="flex items-center gap-1 mt-1">
                        <MapPin className="w-2.5 h-2.5 text-zinc-500 flex-shrink-0" />
                        <span className="text-[10px] text-zinc-500 truncate">{deal.location}</span>
                      </div>
                      <p className="text-[10px] text-zinc-600 mt-0.5">par {deal.user_name}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-8 text-center">
            <Tag className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400">Aucun bon plan pour le moment</p>
          </div>
        )}
      </div>

      {/* Detail Dialog (opens on card click) */}
      <Dialog open={!!selectedDeal} onOpenChange={() => setSelectedDeal(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#2A2A2E] text-white max-w-md max-h-[90vh] overflow-y-auto p-0">
          {selectedDeal && <DealDetail
            deal={deals.find(d => d.id === selectedDeal.id) || selectedDeal}
            user={user}
            orderQty={orderQty} setOrderQty={setOrderQty}
            orderEnveloppe={orderEnveloppe} setOrderEnveloppe={setOrderEnveloppe}
            orderForOther={orderForOther} setOrderForOther={setOrderForOther}
            orderBenefName={orderBenefName} setOrderBenefName={setOrderBenefName}
            onOrder={handleOrder}
            onTogglePaid={handleTogglePaid}
            onLock={(id) => setShowLockConfirm(id)}
            onRecap={handleRecap}
            onEdit={(d) => { setSelectedDeal(null); setEditDeal(d); }}
            onDelete={handleDelete}
            onClose={() => setSelectedDeal(null)}
            isDeadlinePassed={isDeadlinePassed}
          />}
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Nouveau bon plan</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div><Label className="text-zinc-400 text-xs">Produit *</Label><Input value={form.product_name} onChange={e => setForm({...form, product_name: e.target.value})} required placeholder="Filet de poulet..." className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-zinc-400 text-xs">Prix (XPF)</Label><Input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" /></div>
              <div><Label className="text-zinc-400 text-xs">Unite</Label><select value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} className="w-full h-10 bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-md px-2 text-sm"><option value="kg">kg</option><option value="piece">piece</option><option value="boite">boite</option><option value="lot">lot</option><option value="barquette">barquette</option></select></div>
              <div className="flex items-end pb-1"><label className="flex items-center gap-2"><input type="checkbox" checked={form.is_group_order} onChange={e => setForm({...form, is_group_order: e.target.checked})} className="w-4 h-4 accent-[#FF6B35]" /><span className="text-xs text-zinc-400">Groupee</span></label></div>
            </div>
            <div><Label className="text-zinc-400 text-xs">Lieu *</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} required placeholder="Champion Punaauia..." className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
            {form.is_group_order && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-zinc-400 text-xs flex items-center gap-1"><Clock className="w-3 h-3" />Deadline commande</Label><Input type="datetime-local" value={form.deadline_commande} onChange={e => setForm({...form, deadline_commande: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-xs" /></div>
                <div><Label className="text-zinc-400 text-xs flex items-center gap-1"><Calendar className="w-3 h-3" />Livraison prevue</Label><Input type="date" value={form.deadline_livraison} onChange={e => setForm({...form, deadline_livraison: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-xs" /></div>
              </div>
            )}
            <div><Label className="text-zinc-400 text-xs">Photo</Label><MediaUpload onMediaAdded={url => setForm({...form, image_url: url})} compact />{form.image_url && <MediaPreview urls={[form.image_url]} onRemove={() => setForm({...form, image_url: ''})} />}</div>
            <div><Label className="text-zinc-400 text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Details..." className="bg-[#0F0F0F] border-[#2A2A2E] text-white min-h-[40px]" /></div>
            <DialogFooter className="flex gap-2"><Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button><Button type="submit" className="flex-1 bg-[#FF6B35] text-black">Publier</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDeal} onOpenChange={() => setEditDeal(null)}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Modifier</DialogTitle></DialogHeader>
          {editDeal && (
            <form onSubmit={handleUpdate} className="space-y-3">
              <div><Label className="text-zinc-400 text-xs">Produit</Label><Input value={editDeal.product_name} onChange={e => setEditDeal({...editDeal, product_name: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-zinc-400 text-xs">Prix</Label><Input type="number" value={editDeal.price} onChange={e => setEditDeal({...editDeal, price: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" /></div>
                <div><Label className="text-zinc-400 text-xs">Unite</Label><select value={editDeal.unit} onChange={e => setEditDeal({...editDeal, unit: e.target.value})} className="w-full h-10 bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-md px-2 text-sm"><option value="kg">kg</option><option value="piece">piece</option><option value="boite">boite</option><option value="lot">lot</option><option value="barquette">barquette</option></select></div>
              </div>
              <div><Label className="text-zinc-400 text-xs">Lieu</Label><Input value={editDeal.location} onChange={e => setEditDeal({...editDeal, location: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-zinc-400 text-xs">Deadline commande</Label><Input type="datetime-local" value={editDeal.deadline_commande || ''} onChange={e => setEditDeal({...editDeal, deadline_commande: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-xs" /></div>
                <div><Label className="text-zinc-400 text-xs">Livraison</Label><Input type="date" value={editDeal.deadline_livraison || ''} onChange={e => setEditDeal({...editDeal, deadline_livraison: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-xs" /></div>
              </div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editDeal.is_group_order || false} onChange={e => setEditDeal({...editDeal, is_group_order: e.target.checked})} className="w-4 h-4 accent-[#FF6B35]" /><span className="text-xs text-zinc-400">Commande groupee</span></label>
              <div><Label className="text-zinc-400 text-xs">Description</Label><Textarea value={editDeal.description || ''} onChange={e => setEditDeal({...editDeal, description: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white min-h-[40px]" /></div>
              <DialogFooter className="flex gap-2"><Button type="button" variant="outline" onClick={() => setEditDeal(null)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button><Button type="submit" className="flex-1 bg-[#FF6B35] text-black"><Save className="w-4 h-4 mr-1" />Sauver</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Lock Confirmation */}
      <Dialog open={!!showLockConfirm} onOpenChange={() => setShowLockConfirm(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#c0392b]/30 max-w-sm">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#c0392b]" /> Verrouiller la commande ?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400">Cette action est <strong className="text-white">irreversible</strong>.</p>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => setShowLockConfirm(null)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
            <Button onClick={() => handleLock(showLockConfirm)} className="flex-1 bg-[#c0392b] text-white hover:bg-[#a93226]"><Lock className="w-4 h-4 mr-1" /> Verrouiller</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recap Dialog */}
      <Dialog open={!!showRecap} onOpenChange={() => setShowRecap(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#2A2A2E] max-w-md">
          <DialogHeader><DialogTitle className="text-white">Recapitulatif</DialogTitle></DialogHeader>
          <pre className="text-xs text-zinc-300 whitespace-pre-wrap bg-[#1B1B1B] rounded-lg p-4 max-h-80 overflow-y-auto font-mono">{recapText}</pre>
          <Button onClick={handleCopyRecap} className="w-full bg-[#FF6B35] text-white"><Copy className="w-4 h-4 mr-2" /> Copier le recapitulatif</Button>
        </DialogContent>
      </Dialog>

      {!embedded && <BottomNav />}
    </div>
  );
}


function DealDetail({ deal, user, orderQty, setOrderQty, orderEnveloppe, setOrderEnveloppe, orderForOther, setOrderForOther, orderBenefName, setOrderBenefName, onOrder, onTogglePaid, onLock, onRecap, onEdit, onDelete, onClose, isDeadlinePassed }) {
  const orders = deal.orders || [];
  const totalQty = orders.reduce((s, o) => s + (o.quantity || 0), 0);
  const myOrder = orders.find(o => o.user_id === user?.id);
  const isMine = deal.user_id === user?.id;
  const isLocked = deal.locked;
  const deadlinePassed = isDeadlinePassed(deal);
  const canOrder = !isLocked && !deadlinePassed;
  const paidCount = orders.filter(o => o.paid).length;
  const totalAmount = totalQty * deal.price;
  const paidAmount = orders.filter(o => o.paid).reduce((s, o) => s + (o.quantity || 0) * deal.price, 0);

  return (
    <div data-testid={`deal-detail-${deal.id}`}>
      {/* Hero image */}
      {deal.image_url ? (
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={deal.image_url} alt={deal.product_name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <h2 className="text-xl font-bold text-white">{deal.product_name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-lg font-bold text-[#FF6B35]">{deal.price} XPF/{deal.unit}</span>
              <span className="text-xs text-zinc-400">· par {deal.user_name}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-5 pt-5 pb-2">
          <h2 className="text-xl font-bold text-white">{deal.product_name}</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-mono text-lg font-bold text-[#FF6B35]">{deal.price} XPF/{deal.unit}</span>
            <span className="text-xs text-zinc-400">· par {deal.user_name}</span>
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 text-[10px]">
          <span className="flex items-center gap-1 text-zinc-400 bg-[#1B1B1B] px-2 py-1 rounded-lg"><MapPin className="w-3 h-3" />{deal.location}</span>
          {deal.deadline_livraison && <span className="flex items-center gap-1 bg-[#FF6B35]/10 text-[#FF6B35] px-2 py-1 rounded-lg"><Calendar className="w-3 h-3" />Livraison {deal.deadline_livraison}</span>}
          {isLocked && <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded-lg font-semibold">Verrouillee</span>}
          {deadlinePassed && !isLocked && <span className="bg-[#FFB800]/20 text-[#FFB800] px-2 py-1 rounded-lg">Deadline passee</span>}
          {deal.deadline_commande && !deadlinePassed && !isLocked && (
            <span className="flex items-center gap-1 text-zinc-500 bg-[#1B1B1B] px-2 py-1 rounded-lg"><Clock className="w-3 h-3" />Avant le {new Date(deal.deadline_commande).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
          )}
        </div>

        {deal.description && <p className="text-xs text-zinc-400 leading-relaxed">{deal.description}</p>}

        {/* Group Order Section */}
        {deal.is_group_order && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-xl p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-400 flex items-center gap-1"><Package className="w-3 h-3" />{orders.length} commande{orders.length > 1 ? 's' : ''} · {totalQty} {deal.unit}</span>
              <span className="text-xs font-mono text-zinc-300">{totalAmount.toFixed(0)} XPF</span>
            </div>

            {/* Payment bar */}
            {orders.length > 0 && (isMine || user?.role === 'admin') && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[10px]">
                  <span className={paidCount === orders.length ? 'text-green-400 font-semibold' : 'text-[#FFB800]'}>
                    {paidCount}/{orders.length} payé{paidCount > 1 ? 's' : ''}
                  </span>
                  <span className="font-mono text-zinc-500">{paidAmount.toFixed(0)} / {totalAmount.toFixed(0)} XPF</span>
                </div>
                <div className="h-1.5 bg-[#0F0F0F] rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${orders.length ? (paidCount / orders.length * 100) : 0}%` }} />
                </div>
              </div>
            )}

            {/* Orders list */}
            {orders.length > 0 && (
              <div className="space-y-1.5">
                {orders.map((o) => (
                  <div key={o.user_id} className="flex items-center justify-between text-xs gap-2 py-1">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {(isMine || user?.role === 'admin') && !isLocked && (
                        <button onClick={() => onTogglePaid(deal.id, o.user_id)}
                          className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${o.paid ? 'bg-green-500 text-white' : 'border-2 border-zinc-600 hover:border-zinc-400'}`}
                          data-testid={`paid-toggle-${deal.id}-${o.user_id}`}>
                          {o.paid && <Check className="w-3 h-3" />}
                        </button>
                      )}
                      {isLocked && <span className="text-[10px]">{o.paid ? '✅' : '❌'}</span>}
                      <span className={`truncate ${o.user_id === user?.id ? 'text-[#FF6B35] font-medium' : o.paid ? 'text-green-400' : 'text-zinc-400'}`}>
                        {o.user_id === user?.id ? 'Moi' : o.name}
                        {o.beneficiaire_externe && <span className="text-[#3b82f6]"> → {o.beneficiaire_externe}</span>}
                      </span>
                    </div>
                    <span className="font-mono text-zinc-300 flex-shrink-0 text-[11px]">{o.quantity} {deal.unit} · {(o.quantity * deal.price).toFixed(0)} XPF{o.is_enveloppe ? ' ✉️' : ''}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Order form */}
            {canOrder && (
              <div className="space-y-2 pt-2 border-t border-[#2A2A2E]/50">
                <div className="flex items-center gap-2">
                  <button onClick={() => setOrderQty({ ...orderQty, [deal.id]: Math.max(1, (orderQty[deal.id] || 1) - 1) })} className="w-8 h-8 rounded-lg bg-zinc-800 text-white flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                  <span className="font-mono text-white text-sm w-12 text-center">{orderQty[deal.id] || 1}</span>
                  <button onClick={() => setOrderQty({ ...orderQty, [deal.id]: (orderQty[deal.id] || 1) + 1 })} className="w-8 h-8 rounded-lg bg-zinc-800 text-white flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                  <span className="text-xs text-zinc-400">{deal.unit}</span>
                  <Button onClick={() => onOrder(deal.id)} className="ml-auto bg-[#FF6B35] text-black hover:bg-[#E85A25]" size="sm">
                    {myOrder ? <><Check className="w-3 h-3 mr-1" />Modifier</> : <><ShoppingCart className="w-3 h-3 mr-1" />Commander</>}
                  </Button>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={orderEnveloppe[deal.id] || false} onChange={e => setOrderEnveloppe({...orderEnveloppe, [deal.id]: e.target.checked})} className="w-4 h-4 accent-[#FFB800]" />
                  <Mail className="w-3.5 h-3.5 text-[#FFB800]" />
                  <span className="text-xs text-zinc-300">Emballé</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={orderForOther[deal.id] || false} onChange={e => setOrderForOther({...orderForOther, [deal.id]: e.target.checked})} className="w-4 h-4 accent-[#3b82f6]" />
                  <Users className="w-3.5 h-3.5 text-[#3b82f6]" />
                  <span className="text-xs text-zinc-300">Pour quelqu'un d'autre</span>
                </label>
                {orderForOther[deal.id] && (
                  <Input value={orderBenefName[deal.id] || ''} onChange={e => setOrderBenefName({...orderBenefName, [deal.id]: e.target.value})}
                    placeholder="Prenom du beneficiaire" className="bg-[#0F0F0F] border-[#2A2A2E] text-white h-8 text-xs" />
                )}
              </div>
            )}

            {/* Creator actions */}
            {(isMine || user?.role === 'admin') && (
              <div className="pt-2 border-t border-[#2A2A2E]/50 flex gap-2">
                {!isLocked && (
                  <button onClick={() => onLock(deal.id)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30">
                    <Lock className="w-3 h-3" /> Verrouiller
                  </button>
                )}
                <button onClick={() => onRecap(deal.id)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35]/15 text-[#FF6B35] hover:bg-[#FF6B35]/25">
                  <Copy className="w-3 h-3" /> Recap
                </button>
              </div>
            )}
          </div>
        )}

        {/* Bottom actions */}
        <div className="flex items-center justify-between pt-2">
          {(isMine || user?.role === 'admin') && (
            <div className="flex gap-2">
              {!isLocked && <button onClick={() => onEdit({ ...deal })} className="text-zinc-500 hover:text-[#FF6B35] text-xs flex items-center gap-1"><Pencil className="w-3 h-3" /> Modifier</button>}
              <button onClick={() => onDelete(deal.id)} className="text-zinc-500 hover:text-red-400 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Supprimer</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
